import { useState } from 'react';
import { calculateFourPillars, calculateElementBalance, analyzeElements, calculateTenGods } from '../../../utils/fourPillars';

interface BirthSajuResult {
  birthDate: string;
  birthTime: string;
  dayStem: string;
  dayBranch: string;
  elementBalance: { wood: number; fire: number; earth: number; metal: number; water: number };
  strongest: string;
  weakest: string;
  description: string;
  dominantGods: { god: string; count: number; shortMeaning: string }[];
  sajuSummary: string;
}

interface Props {
  onResult: (result: BirthSajuResult) => void;
  result: BirthSajuResult | null;
}

const ELEMENT_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string; ko: string }> = {
  wood: { label: '목(木)', color: 'text-emerald-400', bg: 'bg-emerald-500', icon: '🌿', ko: '목' },
  fire: { label: '화(火)', color: 'text-orange-400', bg: 'bg-orange-500', icon: '🔥', ko: '화' },
  earth: { label: '토(土)', color: 'text-yellow-400', bg: 'bg-yellow-500', icon: '🌍', ko: '토' },
  metal: { label: '금(金)', color: 'text-slate-300', bg: 'bg-slate-400', icon: '⚔️', ko: '금' },
  water: { label: '수(水)', color: 'text-sky-400', bg: 'bg-sky-500', icon: '💧', ko: '수' },
};

const DAY_STEM_PERSONALITY: Record<string, { title: string; desc: string; decision: string }> = {
  갑: { title: '갑목(甲木) — 대나무형 리더', desc: '강인하고 진취적. 선구자 기질로 새로운 길을 개척합니다.', decision: '창업·진로 전환에서 두각을 나타낼 사주입니다. 봄철(寅卯月) 에너지와 맞물리면 강력한 도약이 가능합니다.' },
  을: { title: '을목(乙木) — 넝쿨형 유연인', desc: '유연하지만 내면이 강인. 어떤 환경도 극복하는 생명력을 가집니다.', decision: '직업과 인간관계에서 네트워킹이 핵심 전략입니다. 귀인(貴人) 운이 결정적 변곡점이 됩니다.' },
  병: { title: '병화(丙火) — 태양형 카리스마', desc: '열정적이고 카리스마 넘침. 주변을 밝히는 에너지를 가집니다.', decision: '대중을 상대하는 사업이나 무대에서 최고 성과를 냅니다. 여름(巳午月)에 모든 에너지가 극대화됩니다.' },
  정: { title: '정화(丁火) — 촛불형 집중인', desc: '섬세하고 집중력 탁월. 하나에 깊게 파고드는 전문가형입니다.', decision: '전문직이나 예술 분야에서 꾸준함이 빛을 발합니다. 조급함을 버리면 만기필성(晩期必成)의 사주입니다.' },
  무: { title: '무토(戊土) — 산악형 포용인', desc: '묵직하고 신뢰감 있음. 주변을 품어안는 대인 관계의 달인입니다.', decision: '중재·조정·부동산·건설 분야와 궁합이 좋습니다. 신중한 결정이 항상 큰 안정을 가져옵니다.' },
  기: { title: '기토(己土) — 전원형 실용인', desc: '현실적이고 실용적. 조용히 성과를 쌓아가는 내공의 사주입니다.', decision: '투자와 저축에서 놀라운 복이 발현됩니다. 재성(財星)이 강하면 부동산이나 전통 비즈니스가 최적입니다.' },
  경: { title: '경금(庚金) — 검형 결단인', desc: '날카롭고 결단력 있음. 흑백이 명확하고 원칙을 중시합니다.', decision: '법률·군사·금융·엔지니어링 분야에서 탁월합니다. 목기운(木) 대운이 올 때 최대 도약의 기회가 옵니다.' },
  신: { title: '신금(辛金) — 보석형 완성인', desc: '섬세하고 완벽주의. 아름다움과 정밀함을 추구합니다.', decision: '예술·뷰티·정밀 산업에서 두각을 나타냅니다. 수(水) 기운이 보충될 때 지혜와 결과물이 함께 빛납니다.' },
  임: { title: '임수(壬水) — 강형 전략인', desc: '지혜롭고 전략적. 큰 그림을 그리는 비전가 기질입니다.', decision: '금융·기획·국제 비즈니스에서 강점을 발휘합니다. 화(火) 기운 대운에서 재물과 명예가 동시에 상승합니다.' },
  계: { title: '계수(癸水) — 이슬형 감성인', desc: '감성적이고 직관력 뛰어남. 조용히 스며들며 영향력을 넓힙니다.', decision: '콘텐츠·상담·연구·치유 분야와 천생연분입니다. 봄(木운)이 오면 감성이 결실로 이어지는 시기입니다.' },
};

export type { BirthSajuResult };

export default function BirthSajuPanel({ onResult, result }: Props) {
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('12:00');
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!result);

  const handleCalculate = () => {
    if (!birthDate) return;
    setIsCalculating(true);

    setTimeout(() => {
      const fp = calculateFourPillars(birthDate, birthTime);
      const balance = calculateElementBalance(fp);
      const analysis = analyzeElements(balance);
      const tenGods = calculateTenGods(fp);

      const personality = DAY_STEM_PERSONALITY[fp.day.stem];
      const sajuSummary = personality
        ? `${personality.title} — ${personality.desc} ${personality.decision}`
        : `${fp.day.stem}${fp.day.branch} 일주 사주입니다.`;

      const newResult: BirthSajuResult = {
        birthDate,
        birthTime,
        dayStem: fp.day.stem,
        dayBranch: fp.day.branch,
        elementBalance: balance,
        strongest: analysis.strongest,
        weakest: analysis.weakest,
        description: analysis.description,
        dominantGods: tenGods.dominantGods,
        sajuSummary,
      };

      onResult(newResult);
      setIsCalculating(false);
      setIsExpanded(false);
    }, 1200);
  };

  const total = result
    ? Object.values(result.elementBalance).reduce((a, b) => a + b, 0) || 1
    : 1;

  return (
    <div className="mb-6 rounded-2xl border border-primary-gold/30 overflow-hidden">
      {/* Header toggle */}
      <button
        onClick={() => setIsExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-amber-900/30 to-amber-800/20 hover:from-amber-800/40 transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-gold/20">
            <i className="ri-calendar-2-line text-primary-gold text-sm"></i>
          </div>
          <div className="text-left">
            <p className="text-white text-sm font-bold">
              {result ? `내 사주 · ${result.dayStem}${result.dayBranch} 일주` : '생년월일로 개인 맞춤 분석'}
            </p>
            <p className="text-amber-400/60 text-xs">
              {result ? `오행 ${result.strongest} 최강 · ${result.weakest} 보완 필요` : '입력하면 실제 사주 오행 기반 분석 제공'}
            </p>
          </div>
        </div>
        <div className="w-6 h-6 flex items-center justify-center">
          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-amber-400 text-base transition-transform`}></i>
        </div>
      </button>

      {/* Input form */}
      {isExpanded && (
        <div className="px-5 py-4 bg-primary-dark/60 border-t border-primary-gold/15">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-purple-300 text-xs mb-1.5 block">생년월일 <span className="text-red-400">*</span></label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm focus:border-primary-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="text-purple-300 text-xs mb-1.5 block">태어난 시각</label>
              <input
                type="time"
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm focus:border-primary-gold focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={handleCalculate}
            disabled={!birthDate || isCalculating}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-bold text-sm hover:shadow-lg hover:shadow-amber-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {isCalculating ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                <span>사주 계산 중...</span>
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-flashlight-line text-sm"></i>
                </div>
                <span>내 사주 오행 계산</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Result panel */}
      {result && !isExpanded && (
        <div className="px-5 py-4 bg-primary-dark/40 border-t border-primary-gold/15">
          {/* Element bars */}
          <p className="text-purple-300/60 text-xs mb-2.5 font-semibold uppercase tracking-wide">오행 분포</p>
          <div className="space-y-1.5 mb-3">
            {Object.entries(ELEMENT_CONFIG).map(([key, cfg]) => {
              const val = result.elementBalance[key as keyof typeof result.elementBalance];
              const pct = Math.round((val / total) * 100);
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs w-11 flex-shrink-0 text-right font-medium" style={{ color: cfg.color.includes('emerald') ? '#34d399' : cfg.color.includes('orange') ? '#fb923c' : cfg.color.includes('yellow') ? '#facc15' : cfg.color.includes('slate') ? '#cbd5e1' : '#38bdf8' }}>
                    {cfg.icon} {cfg.label}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-white/5">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${cfg.bg} opacity-80`}
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-purple-400 w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>

          {/* Dominant Gods */}
          {result.dominantGods.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {result.dominantGods.map((g) => (
                <span key={g.god} className="text-[10px] px-2 py-0.5 rounded-full bg-primary-gold/10 border border-primary-gold/25 text-primary-gold font-medium">
                  {g.god}({g.count}) · {g.shortMeaning}
                </span>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-amber-900/20 to-amber-800/10 border border-amber-500/20">
            <p className="text-amber-200/80 text-xs leading-relaxed">{result.sajuSummary}</p>
          </div>
        </div>
      )}
    </div>
  );
}
